import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import InlineFeedback from '../components/InlineFeedback';
import StripePaymentForm from '../components/StripePaymentForm';

const resolveApiBaseUrl = () => {
  const envBase = process.env.REACT_APP_API_BASE_URL;
  if (envBase && envBase.trim()) {
    return envBase.trim().replace(/\/+$/, '');
  }

  if (typeof window !== 'undefined' && window.location?.hostname) {
    const currentHost = window.location.hostname;
    if (currentHost === 'localhost' || currentHost === '::1' || currentHost === '[::1]') {
      return 'http://127.0.0.1:8000/api';
    }
    return `http://${currentHost}:8000/api`;
  }

  return 'http://127.0.0.1:8000/api';
};

const API_BASE_URL = resolveApiBaseUrl();
const apiUrl = (path = '') => `${API_BASE_URL}/${String(path).replace(/^\/+/, '')}`;
const STATUS_ORDER = ['confirmed', 'pending', 'completed', 'canceled', 'no_show', 'failed', 'unknown'];

const MyBookings = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = localStorage.getItem('token');
  const [bookings, setBookings] = useState([]);
  const [role, setRole] = useState('');
  const [message, setMessage] = useState('');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [modalService, setModalService] = useState(null);
  const [reviewText, setReviewText] = useState('');
  const [reviewRating, setReviewRating] = useState(5);
  const [cancelReason, setCancelReason] = useState('');
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [rescheduleSlotsByDate, setRescheduleSlotsByDate] = useState({});
  const [rescheduleAvailableDates, setRescheduleAvailableDates] = useState([]);
  const [rescheduleAvailableTimes, setRescheduleAvailableTimes] = useState([]);
  const [rescheduleOptionsError, setRescheduleOptionsError] = useState('');
  const [isRescheduleOptionsLoading, setIsRescheduleOptionsLoading] = useState(false);
  const [actionError, setActionError] = useState('');
  const [isActionSubmitting, setIsActionSubmitting] = useState(false);
  const [isPaymentSubmitting, setIsPaymentSubmitting] = useState(false);
  const [isFinalPaymentSubmitting, setIsFinalPaymentSubmitting] = useState(false);
  const [paymentCheckout, setPaymentCheckout] = useState(null);
  const [tipAmount, setTipAmount] = useState('');
  const [isTipSubmitting, setIsTipSubmitting] = useState(false);
  const [proposalDate, setProposalDate] = useState('');
  const [proposalTime, setProposalTime] = useState('');
  const [proposalReason, setProposalReason] = useState('');
  const [proposalResponseReason, setProposalResponseReason] = useState('');
  const [isProposalSubmitting, setIsProposalSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeStatusFilter, setActiveStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('soonest');
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isModalLoading, setIsModalLoading] = useState(false);

  const buildAuthHeaders = useCallback((accessToken = token) => ({
    headers: { Authorization: `Bearer ${accessToken}` },
  }), [token]);

  const clearSessionAndRedirectToLogin = useCallback((reasonMessage = 'Session expired. Please log in again.') => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userRole');
    window.dispatchEvent(new Event('auth-changed'));
    setMessage(`❌ ${reasonMessage}`);
    navigate('/login');
  }, [navigate]);

  const statusActionLabels = {
    confirmed: '✅ Confirm Booking',
    completed: '🟢 Mark Completed',
    canceled: '❌ Cancel Booking',
    no_show: '🚫 Mark No Show',
    failed: '⚠️ Mark Failed',
  };

  const normalizeBooking = (booking) => {
    const rawServices = Array.isArray(booking?.services) ? booking.services : [];
    const serviceNames = rawServices
      .map((service) => {
        if (typeof service === 'string') return service;
        if (service && typeof service === 'object') return service.name;
        return null;
      })
      .filter(Boolean);

    const serviceIds = rawServices
      .map((service) => {
        if (typeof service === 'number') return service;
        if (service && typeof service === 'object') return service.id;
        return null;
      })
      .filter((id) => id !== null && id !== undefined);

    const normalizedStatus =
      booking?.status && typeof booking.status === 'object'
        ? booking.status
        : {
          current_status: booking?.status || 'unknown',
          status_history: [],
        };

    const paymentSummary = booking.payment_summary || {};
    const balanceSummary = booking.balance_summary || {};
    const proposalSummary = booking.proposal_summary || {};
    const tipSummary = booking.tip_summary || {};

    return {
      ...booking,
      services: serviceNames.join(', ') || 'No service selected',
      service_ids: serviceIds,
      status: normalizedStatus,
      allowed_status_transitions: booking.allowed_status_transitions || [],
      can_reschedule: Boolean(booking.can_reschedule),
      lifecycle_policy: booking.lifecycle_policy || {
        cancel_message: 'Cancellation policy details are currently unavailable.',
        reschedule_message: 'Rescheduling policy details are currently unavailable.',
      },
      payment_summary: {
        required: Boolean(paymentSummary.required),
        status: paymentSummary.status || booking.payment_status || 'not_required',
        currency: paymentSummary.currency || booking.currency || 'usd',
        total_amount_cents: paymentSummary.total_amount_cents || booking.total_amount_cents || 0,
        deposit_amount_cents: paymentSummary.deposit_amount_cents || booking.deposit_amount_cents || 0,
        payment_due_at: paymentSummary.payment_due_at || booking.payment_due_at || null,
        refunded_amount_cents: paymentSummary.refunded_amount_cents || 0,
        refundable_amount_cents: paymentSummary.refundable_amount_cents || 0,
        refundable_payment_id: paymentSummary.refundable_payment_id || null,
        refundable_payment_type: paymentSummary.refundable_payment_type || null,
        refund_status: paymentSummary.refund_status || 'none',
        latest_adjustment: paymentSummary.latest_adjustment || null,
        latest_payment: paymentSummary.latest_payment || null,
      },
      balance_summary: {
        status: balanceSummary.status || 'not_required',
        currency: balanceSummary.currency || booking.currency || 'usd',
        service_total_cents: balanceSummary.service_total_cents || booking.total_amount_cents || 0,
        deposit_paid_cents: balanceSummary.deposit_paid_cents || 0,
        final_paid_cents: balanceSummary.final_paid_cents || 0,
        service_paid_cents: balanceSummary.service_paid_cents || 0,
        amount_due_cents: balanceSummary.amount_due_cents || 0,
        can_pay: Boolean(balanceSummary.can_pay),
        latest_payment: balanceSummary.latest_payment || null,
      },
      tip_summary: {
        status: tipSummary.status || booking.tip_status || 'not_required',
        amount_cents: tipSummary.amount_cents || booking.tip_amount_cents || 0,
        currency: tipSummary.currency || booking.currency || 'usd',
        tipped_at: tipSummary.tipped_at || booking.tipped_at || null,
        can_tip: Boolean(tipSummary.can_tip),
        latest_payment: tipSummary.latest_payment || null,
      },
      proposal_summary: {
        status: proposalSummary.status || booking.proposal_status || 'none',
        proposed_date: proposalSummary.proposed_date || booking.proposed_date || null,
        proposed_time: proposalSummary.proposed_time || booking.proposed_time || null,
        reason: proposalSummary.reason || booking.proposal_reason || '',
        updated_at: proposalSummary.updated_at || booking.proposal_updated_at || null,
        proposed_by: proposalSummary.proposed_by || null,
        can_respond: Boolean(proposalSummary.can_respond),
      },
    };
  };

  const extractErrorMessage = (error, fallback) => {
    const apiError = error?.response?.data?.error;
    if (typeof apiError === 'string' && apiError.trim()) {
      return apiError;
    }
    return fallback;
  };

  const formatCurrencyFromCents = (value, currency = 'USD') => {
    const amount = (Number(value || 0) || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: String(currency || 'USD').toUpperCase(),
      }).format(amount);
    } catch {
      return `${amount.toFixed(2)} ${String(currency || 'USD').toUpperCase()}`;
    }
  };

  const handleRescheduleDateChange = (dateValue) => {
    setRescheduleDate(dateValue);
    const times = rescheduleSlotsByDate[dateValue] || [];
    setRescheduleAvailableTimes(times);
    setRescheduleTime(times[0] || '');
    setActionError('');
  };

  const loadRescheduleAvailability = async (booking) => {
    if (!booking?.can_reschedule || !booking?.worker?.id) {
      setRescheduleSlotsByDate({});
      setRescheduleAvailableDates([]);
      setRescheduleAvailableTimes([]);
      setRescheduleDate('');
      setRescheduleTime('');
      setRescheduleOptionsError('');
      return;
    }

    try {
      setIsRescheduleOptionsLoading(true);
      setRescheduleOptionsError('');

      const scheduleRes = await axios.get(
        `${apiUrl('schedules/')}?worker_id=${booking.worker.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const todayKey = new Date().toISOString().split('T')[0];
      const scheduleRows = scheduleRes.data || [];
      const candidateDates = scheduleRows
        .filter((row) => row?.date >= todayKey && ((row.working_windows || []).length || (row.time_slots || []).length))
        .map((row) => row.date)
        .sort();
      const availabilityResponses = await Promise.all(candidateDates.map((date) => (
        axios.get(`${apiUrl(`bookings/${booking.id}/availability/`)}?date=${date}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
      )));
      const slotsByDate = availabilityResponses.reduce((result, response) => {
        const row = response.data || {};
        if ((row.available_start_times || []).length) result[row.date] = row.available_start_times;
        return result;
      }, {});
      const dates = Object.keys(slotsByDate).sort();
      setRescheduleSlotsByDate(slotsByDate);
      setRescheduleAvailableDates(dates);

      if (!dates.length) {
        setRescheduleDate('');
        setRescheduleTime('');
        setRescheduleAvailableTimes([]);
        return;
      }

      const preferredDate = dates.includes(booking.date) ? booking.date : dates[0];
      const timesForDate = slotsByDate[preferredDate] || [];
      const preferredTime = String(booking.time || '').slice(0, 5);

      setRescheduleDate(preferredDate);
      setRescheduleAvailableTimes(timesForDate);
      setRescheduleTime(timesForDate.includes(preferredTime) ? preferredTime : (timesForDate[0] || ''));
    } catch (error) {
      console.error('Failed to load reschedule availability:', error);
      setRescheduleOptionsError('Unable to load worker availability right now.');
      setRescheduleSlotsByDate({});
      setRescheduleAvailableDates([]);
      setRescheduleAvailableTimes([]);
      setRescheduleDate('');
      setRescheduleTime('');
    } finally {
      setIsRescheduleOptionsLoading(false);
    }
  };

  const fetchBookings = useCallback(async (accessToken = token) => {
    const bookingsRes = await axios.get(apiUrl('bookings/'), buildAuthHeaders(accessToken));

    const payload = bookingsRes.data;
    const bookingRows = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.results)
        ? payload.results
        : [];

    setBookings(bookingRows.map(normalizeBooking));
  }, [token, buildAuthHeaders]);

  useEffect(() => {
    if (!token) {
      setMessage('❌ Please log in to view your bookings.');
      setIsPageLoading(false);
      navigate('/login');
      return;
    }

    const fetchData = async () => {
      setIsPageLoading(true);
      try {
        const activeToken = localStorage.getItem('token');
        const userRes = await axios.get(apiUrl('userprofiles/me/'), buildAuthHeaders(activeToken));
        setRole(userRes.data.role);
        await fetchBookings(activeToken);
      } catch (error) {
        console.error('Failed to fetch bookings:', error);

        const statusCode = error?.response?.status;
        const errorCode = error?.response?.data?.code;
        if (statusCode === 401 || errorCode === 'token_not_valid') {
          const refreshToken = localStorage.getItem('refreshToken');
          if (refreshToken) {
            try {
              const refreshResponse = await axios.post(
                apiUrl('token/refresh/'),
                { refresh: refreshToken }
              );
              const refreshedAccessToken = refreshResponse?.data?.access;
              if (refreshedAccessToken) {
                localStorage.setItem('token', refreshedAccessToken);
                window.dispatchEvent(new Event('auth-changed'));

                const retryUserRes = await axios.get(
                  apiUrl('userprofiles/me/'),
                  buildAuthHeaders(refreshedAccessToken),
                );
                setRole(retryUserRes.data.role);
                await fetchBookings(refreshedAccessToken);
                setMessage('');
                return;
              }
            } catch (refreshError) {
              console.error('Token refresh failed while loading My Bookings:', refreshError);
            }
          }

          clearSessionAndRedirectToLogin('Session expired. Please log in again.');
          return;
        }

        const apiMessage = error?.response?.data?.error || error?.response?.data?.detail;
        if (apiMessage) {
          setMessage(`❌ ${apiMessage}`);
        } else if (error?.response) {
          const statusHint = error.response.status ? ` (HTTP ${error.response.status})` : '';
          setMessage(`❌ Backend request failed while loading your profile${statusHint}. Please try again or log in again.`);
        } else if (error?.request) {
          const endpointHint = apiUrl('userprofiles/me/');
          const errorCodeHint = error?.code ? ` (code: ${error.code})` : '';
          setMessage(`❌ Unable to reach the backend at ${endpointHint}${errorCodeHint}.`);
        } else {
          setMessage('❌ Unable to load bookings right now. Please try again.');
        }
      } finally {
        setIsPageLoading(false);
      }
    };

    fetchData();
  }, [token, navigate, fetchBookings, buildAuthHeaders, clearSessionAndRedirectToLogin]);

  useEffect(() => {
    const returnedFromStripe = searchParams.get('payment_return') === '1';
    const submittedWithoutRedirect = searchParams.get('payment_submitted') === '1';
    if (!returnedFromStripe && !submittedWithoutRedirect) return;

    const returnedType = searchParams.get('payment_type');
    const returnedPaymentType = returnedType === 'tip'
      ? 'Tip'
      : returnedType === 'final'
        ? 'Balance payment'
        : 'Payment';
    setMessage(returnedFromStripe
      ? `✅ Returned from Stripe. ${returnedPaymentType} status will update after signed webhook verification.`
      : `✅ ${returnedPaymentType} submitted. Status will update after signed webhook verification.`);

    // Stripe can append a PaymentIntent client secret to redirect URLs. Remove
    // all payment query parameters immediately; the server webhook remains the
    // source of truth and this page never needs the secret.
    navigate('/myBookings', { replace: true });
  }, [searchParams, navigate]);

  const handleOpenModal = async (booking) => {
    setIsModalLoading(true);
    try {
      const [serviceId] = booking.service_ids;

      const [serviceRes, reviewRes] = await Promise.all([
        axios.get(apiUrl(`services/${serviceId}/`), {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${apiUrl('reviews/')}?booking=${booking.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      const review = reviewRes.data?.find(
        (item) => Number(item.booking) === Number(booking.id),
      ) || null;

      setSelectedBooking({ ...booking, review });
      setCancelReason('');
      setRescheduleReason('');
      setRescheduleDate('');
      setRescheduleTime('');
      setRescheduleSlotsByDate({});
      setRescheduleAvailableDates([]);
      setRescheduleAvailableTimes([]);
      setRescheduleOptionsError('');
      setActionError('');
      setPaymentCheckout(null);
      setTipAmount('');
      setProposalDate(booking?.proposal_summary?.proposed_date || booking?.date || '');
      setProposalTime(String(booking?.proposal_summary?.proposed_time || booking?.time || '').slice(0, 5));
      setProposalReason('');
      setProposalResponseReason('');
      setModalService(serviceRes.data);

      await loadRescheduleAvailability(booking);
    } catch (err) {
      console.error('Error loading modal content:', err);
    } finally {
      setIsModalLoading(false);
    }
  };

  const handleCloseModal = () => {
    setSelectedBooking(null);
    setModalService(null);
    setReviewText('');
    setReviewRating(5);
    setCancelReason('');
    setRescheduleReason('');
    setRescheduleDate('');
    setRescheduleTime('');
    setRescheduleSlotsByDate({});
    setRescheduleAvailableDates([]);
    setRescheduleAvailableTimes([]);
    setRescheduleOptionsError('');
    setIsRescheduleOptionsLoading(false);
    setActionError('');
    setIsActionSubmitting(false);
    setIsPaymentSubmitting(false);
    setIsFinalPaymentSubmitting(false);
    setPaymentCheckout(null);
    setTipAmount('');
    setIsTipSubmitting(false);
    setProposalDate('');
    setProposalTime('');
    setProposalReason('');
    setProposalResponseReason('');
    setIsProposalSubmitting(false);
  };

  const handleInitializePayment = async () => {
    if (!selectedBooking) return;

    try {
      setActionError('');
      setIsPaymentSubmitting(true);

      const response = await axios.post(
        apiUrl(`bookings/${selectedBooking.id}/initialize_payment/`),
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const payload = response.data || {};
      const payment = payload.payment || {};
      setPaymentCheckout({ ...payload, payment_type: 'deposit' });

      setSelectedBooking((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          payment_summary: {
            ...(prev.payment_summary || {}),
            status: payload.booking_payment_status || prev.payment_summary?.status || 'pending',
            latest_payment: {
              ...(prev.payment_summary?.latest_payment || {}),
              id: payment.id,
              status: payment.status,
              amount_cents: payment.amount_cents,
              currency: payment.currency,
              stripe_payment_intent_id: payment.payment_intent_id,
            },
          },
        };
      });

      await fetchBookings();
      setMessage('✅ Deposit payment initialized. Complete payment via Stripe to finalize confirmation eligibility.');
    } catch (error) {
      setActionError(extractErrorMessage(error, 'Unable to initialize payment for this booking.'));
    } finally {
      setIsPaymentSubmitting(false);
    }
  };

  const handleStripePaymentSubmitted = async ({ status: stripeStatus }) => {
    const paymentName = paymentCheckout?.payment_type === 'tip'
      ? 'Tip'
      : paymentCheckout?.payment_type === 'final'
        ? 'Balance payment'
        : 'Deposit';
    setMessage(`✅ ${paymentName} submitted to Stripe (${stripeStatus}). Waiting for signed webhook verification.`);
    try {
      await fetchBookings();
    } catch {
      setActionError('Payment was submitted, but booking status could not be refreshed yet. Reload My Bookings shortly.');
    }
  };

  const handleInitializeFinalPayment = async () => {
    if (!selectedBooking || isFinalPaymentSubmitting || paymentCheckout) return;
    try {
      setActionError('');
      setIsFinalPaymentSubmitting(true);
      const response = await axios.post(
        apiUrl(`bookings/${selectedBooking.id}/initialize_final_payment/`),
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const payload = response.data || {};
      setPaymentCheckout({ ...payload, payment_type: 'final' });
      setSelectedBooking((previous) => previous ? {
        ...previous,
        balance_summary: {
          ...(previous.balance_summary || {}),
          status: payload.payment?.status || 'pending',
          amount_due_cents: payload.payment?.amount_cents || previous.balance_summary?.amount_due_cents || 0,
        },
      } : previous);
      setMessage('✅ Remaining-balance checkout is ready. Complete it securely through Stripe.');
    } catch (error) {
      setActionError(extractErrorMessage(error, 'Unable to initialize the remaining balance.'));
    } finally {
      setIsFinalPaymentSubmitting(false);
    }
  };

  const handleInitializeTip = async () => {
    if (!selectedBooking || isTipSubmitting) return;
    const amountCents = Math.round(Number(tipAmount) * 100);
    if (!Number.isFinite(amountCents) || amountCents < 100) {
      setActionError('Enter a tip of at least $1.00.');
      return;
    }

    try {
      setActionError('');
      setIsTipSubmitting(true);
      const response = await axios.post(
        apiUrl(`bookings/${selectedBooking.id}/initialize_tip/`),
        { amount_cents: amountCents },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const payload = response.data || {};
      setPaymentCheckout({ ...payload, payment_type: 'tip' });
      setSelectedBooking((previous) => previous ? {
        ...previous,
        tip_summary: {
          ...(previous.tip_summary || {}),
          status: payload.booking_tip_status || 'pending',
          amount_cents: payload.payment?.amount_cents || amountCents,
        },
      } : previous);
      setMessage('✅ Tip checkout is ready. Complete it securely through Stripe.');
    } catch (error) {
      setActionError(extractErrorMessage(error, 'Unable to initialize this tip.'));
    } finally {
      setIsTipSubmitting(false);
    }
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(apiUrl('reviews/'), {
        booking: selectedBooking.id,
        rating: reviewRating,
        comment: reviewText,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessage('✅ Review submitted!');
      handleCloseModal();
    } catch {
      setMessage('❌ Failed to submit review.');
    }
  };

  const handleUpdateStatus = async (bookingId, newStatus) => {
    try {
      const booking = bookings.find(b => b.id === bookingId);
      if (!booking?.status?.id) return;

      setActionError('');
      setIsActionSubmitting(true);
      await axios.put(
        apiUrl(`booking-status/${booking.status.id}/update-status/`),
        { new_status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      await fetchBookings();
      setMessage(`✅ Booking marked as ${newStatus}`);
      handleCloseModal();
    } catch (error) {
      setActionError(extractErrorMessage(error, 'Failed to update booking status.'));
    } finally {
      setIsActionSubmitting(false);
    }
  };

  const handleCancelBooking = async () => {
    if (!selectedBooking) return;

    try {
      setActionError('');
      setIsActionSubmitting(true);
      await axios.post(
        apiUrl(`bookings/${selectedBooking.id}/cancel/`),
        { reason: cancelReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      await fetchBookings();
      setMessage('✅ Booking canceled successfully.');
      handleCloseModal();
    } catch (error) {
      setActionError(extractErrorMessage(error, 'Failed to cancel booking.'));
    } finally {
      setIsActionSubmitting(false);
    }
  };

  const handleRejectBooking = async () => {
    if (!selectedBooking) return;

    try {
      setActionError('');
      setIsActionSubmitting(true);
      await axios.post(
        apiUrl(`bookings/${selectedBooking.id}/reject/`),
        { reason: cancelReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      await fetchBookings();
      setMessage('✅ Booking rejected successfully.');
      handleCloseModal();
    } catch (error) {
      setActionError(extractErrorMessage(error, 'Failed to reject booking.'));
    } finally {
      setIsActionSubmitting(false);
    }
  };

  const handleRescheduleBooking = async () => {
    if (!selectedBooking) return;

    if (!rescheduleDate || !rescheduleTime) {
      setActionError('Please select both a new date and time from available worker slots.');
      return;
    }

    if (
      !rescheduleAvailableDates.includes(rescheduleDate)
      || !rescheduleAvailableTimes.includes(rescheduleTime)
    ) {
      setActionError('Please choose a date and time from the worker’s available slots.');
      return;
    }

    try {
      setActionError('');
      setIsActionSubmitting(true);
      await axios.post(
        apiUrl(`bookings/${selectedBooking.id}/reschedule/`),
        {
          new_date: rescheduleDate,
          new_time: rescheduleTime,
          reason: rescheduleReason,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      await fetchBookings();
      setMessage('✅ Booking rescheduled successfully.');
      handleCloseModal();
    } catch (error) {
      setActionError(extractErrorMessage(error, 'Failed to reschedule booking.'));
    } finally {
      setIsActionSubmitting(false);
    }
  };

  const handleProposeReschedule = async () => {
    if (!selectedBooking) return;

    if (!proposalDate || !proposalTime) {
      setActionError('Please provide both a proposed date and time.');
      return;
    }

    try {
      setActionError('');
      setIsProposalSubmitting(true);
      const response = await axios.post(
        apiUrl(`bookings/${selectedBooking.id}/propose_reschedule/`),
        {
          new_date: proposalDate,
          new_time: proposalTime,
          reason: proposalReason,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const normalized = normalizeBooking(response.data || {});
      setSelectedBooking((prev) => (prev ? { ...prev, ...normalized, review: prev.review } : normalized));
      await fetchBookings();
      setMessage('✅ New time proposal submitted.');
      setProposalResponseReason('');
    } catch (error) {
      setActionError(extractErrorMessage(error, 'Failed to submit proposal.'));
    } finally {
      setIsProposalSubmitting(false);
    }
  };

  const handleRespondToProposal = async (actionName) => {
    if (!selectedBooking) return;

    try {
      setActionError('');
      setIsProposalSubmitting(true);

      const response = await axios.post(
        apiUrl(`bookings/${selectedBooking.id}/respond_proposal/`),
        {
          action: actionName,
          reason: proposalResponseReason,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const normalized = normalizeBooking(response.data || {});
      setSelectedBooking((prev) => (prev ? { ...prev, ...normalized, review: prev.review } : normalized));
      await fetchBookings();
      setMessage(actionName === 'approve'
        ? '✅ Proposal approved and booking updated.'
        : '✅ Proposal rejected.');
      setProposalReason('');
      setProposalResponseReason('');
    } catch (error) {
      setActionError(extractErrorMessage(error, 'Failed to respond to proposal.'));
    } finally {
      setIsProposalSubmitting(false);
    }
  };

  const getStatusLabel = (status) => ({
    confirmed: '✅ Confirmed',
    pending: '⌛ Pending',
    completed: '🟢 Completed',
    canceled: '❌ Canceled',
    no_show: '🚫 No Show',
    failed: '⚠️ Failed',
    unknown: '❓ Unknown',
  }[status] || status);

  const getStatusBadgeClasses = (status) => ({
    confirmed: 'bg-emerald-100 text-emerald-700',
    pending: 'bg-amber-100 text-amber-700',
    completed: 'bg-blue-100 text-blue-700',
    canceled: 'bg-red-100 text-red-700',
    no_show: 'bg-orange-100 text-orange-700',
    failed: 'bg-rose-100 text-rose-700',
    unknown: 'bg-gray-200 text-gray-700',
  }[status] || 'bg-gray-200 text-gray-700');

  const statusCounts = useMemo(() => {
    const initial = STATUS_ORDER.reduce((acc, status) => ({ ...acc, [status]: 0 }), {});
    return bookings.reduce((acc, booking) => {
      const status = booking.status?.current_status || 'unknown';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, initial);
  }, [bookings]);

  const bookingSearchMatches = useCallback((booking) => {
    if (!searchQuery.trim()) return true;

    const haystack = [
      booking?.client?.first_name,
      booking?.client?.last_name,
      booking?.worker?.first_name,
      booking?.worker?.last_name,
      booking?.services,
      booking?.date,
      String(booking?.time || '').slice(0, 5),
      booking?.status?.current_status,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return haystack.includes(searchQuery.toLowerCase());
  }, [searchQuery]);

  const bookingDateTimestamp = (booking) => {
    const date = booking?.date || '1970-01-01';
    const time = String(booking?.time || '00:00').slice(0, 5);
    const value = new Date(`${date}T${time}:00`).getTime();
    return Number.isNaN(value) ? 0 : value;
  };

  const filteredAndSortedBookings = useMemo(() => {
    const filtered = bookings.filter((booking) => {
      const status = booking.status?.current_status || 'unknown';
      const statusMatch = activeStatusFilter === 'all' || status === activeStatusFilter;
      const searchMatch = bookingSearchMatches(booking);
      return statusMatch && searchMatch;
    });

    return [...filtered].sort((a, b) => {
      if (sortBy === 'latest') return bookingDateTimestamp(b) - bookingDateTimestamp(a);
      if (sortBy === 'status') {
        const left = STATUS_ORDER.indexOf(a.status?.current_status || 'unknown');
        const right = STATUS_ORDER.indexOf(b.status?.current_status || 'unknown');
        return left - right;
      }
      return bookingDateTimestamp(a) - bookingDateTimestamp(b);
    });
  }, [bookings, activeStatusFilter, sortBy, bookingSearchMatches]);

  const grouped = useMemo(() => {
    if (activeStatusFilter !== 'all') {
      return [{ status: activeStatusFilter, items: filteredAndSortedBookings }];
    }

    return STATUS_ORDER
      .map((status) => ({
        status,
        items: filteredAndSortedBookings.filter(
          (booking) => (booking.status?.current_status || 'unknown') === status
        ),
      }))
      .filter((group) => group.items.length > 0);
  }, [filteredAndSortedBookings, activeStatusFilter]);

  const canRejectPendingBooking = Boolean(
    selectedBooking
    && ['worker', 'admin'].includes(role)
    && selectedBooking.status?.current_status === 'pending'
  );

  const isWorkerPendingBooking = Boolean(
    selectedBooking
    && role === 'worker'
    && selectedBooking.status?.current_status === 'pending'
  );

  const canShowCancelSection = Boolean(
    selectedBooking
    && !isWorkerPendingBooking
    && (
      selectedBooking.allowed_status_transitions?.includes('canceled')
      || ['pending', 'confirmed'].includes(selectedBooking.status?.current_status)
    )
  );

  const canSubmitCancel = Boolean(
    selectedBooking
    && selectedBooking.allowed_status_transitions?.includes('canceled')
  );

  const cancelBlockedReason = !selectedBooking || canSubmitCancel
    ? ''
    : `Cancellation is currently unavailable while this booking is in "${selectedBooking.status?.current_status || 'unknown'}" status or due to policy restrictions.`;

  const feedbackType = message.startsWith('❌')
    ? 'error'
    : message.startsWith('⚠️')
      ? 'warning'
      : 'success';

  return (
    <div className="min-h-screen bg-[#F6F4F8] px-3 py-4 text-[#2E2E2E] sm:px-4 sm:py-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <header className="rounded-2xl bg-gradient-to-r from-[#6D3B7E] to-[#8A4AA3] p-4 text-white shadow-lg sm:p-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-2xl font-bold sm:text-3xl">My Bookings</h1>
              <p className="mt-2 text-sm text-purple-100 md:text-base">
                Track every appointment, manage updates quickly, and keep your schedule clear.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
              <button
                onClick={() => navigate('/calendar')}
                className="rounded-lg bg-white/15 px-4 py-2 text-sm font-semibold text-white backdrop-blur hover:bg-white/25"
              >
                📆 Calendar
              </button>
              <button
                onClick={() => navigate('/')}
                className="rounded-lg bg-white/15 px-4 py-2 text-sm font-semibold text-white backdrop-blur hover:bg-white/25"
              >
                🏠 Home
              </button>
              <button
                onClick={() => navigate('/booking')}
                className="rounded-lg bg-[#CDA349] px-4 py-2 text-sm font-semibold text-white hover:bg-[#b8913e]"
              >
                📅 New Booking
              </button>
            </div>
          </div>
        </header>

        {message ? <InlineFeedback type={feedbackType} message={message} /> : null}

        <section className="rounded-2xl bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex-1">
              <label htmlFor="booking-search" className="sr-only">Search bookings</label>
              <input
                id="booking-search"
                type="text"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search by name, service, date, time, or status"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div className="grid grid-cols-[auto_1fr] items-center gap-2 sm:flex">
              <label htmlFor="sort-bookings" className="text-xs font-semibold uppercase tracking-wide text-gray-600">Sort</label>
              <select
                id="sort-bookings"
                value={sortBy}
                onChange={(event) => setSortBy(event.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="soonest">Soonest</option>
                <option value="latest">Latest</option>
                <option value="status">Status</option>
              </select>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setActiveStatusFilter('all')}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                activeStatusFilter === 'all'
                  ? 'bg-[#6D3B7E] text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All ({bookings.length})
            </button>
            {STATUS_ORDER.map((status) => (
              <button
                key={status}
                type="button"
                onClick={() => setActiveStatusFilter(status)}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                  activeStatusFilter === status
                    ? 'bg-[#6D3B7E] text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {getStatusLabel(status)} ({statusCounts[status] || 0})
              </button>
            ))}
          </div>
        </section>

        {isPageLoading ? <InlineFeedback type="info" message="Loading your bookings..." /> : null}

        {!isPageLoading && filteredAndSortedBookings.length === 0 ? (
          <section className="rounded-2xl border border-dashed border-gray-300 bg-white p-10 text-center shadow-sm">
            <h2 className="text-xl font-semibold text-[#6D3B7E]">No bookings found</h2>
            <p className="mt-2 text-sm text-gray-600">
              {bookings.length === 0
                ? 'You have no bookings yet. Create your first appointment to get started.'
                : 'No results match your current search/filter settings.'}
            </p>
          </section>
        ) : null}

        {grouped.map(({ status, items }) => (
          <section key={status} className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-[#4B2A56]">{getStatusLabel(status)}</h2>
              <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-600">
                {items.length} booking{items.length === 1 ? '' : 's'}
              </span>
            </div>
            <ul className="grid gap-4 sm:grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
              {items.map((booking) => (
                <li
                  key={booking.id}
                  className="cursor-pointer rounded-2xl border border-transparent bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-[#E5D4ED] hover:shadow-md"
                  onClick={() => handleOpenModal(booking)}
                >
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="text-base font-semibold text-[#4B2A56]">
                      {role === 'worker'
                        ? `${booking.client?.first_name} ${booking.client?.last_name}`
                        : `${booking.worker?.first_name || 'Unassigned'} ${booking.worker?.last_name || ''}`}
                    </h3>
                    <span
                      className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ${getStatusBadgeClasses(
                        booking.status?.current_status || 'unknown'
                      )}`}
                    >
                      {booking.status?.current_status || 'unknown'}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-gray-600">💇‍♀️ {booking.services}</p>
                  <p className="mt-1 text-sm text-gray-600">
                    📅 {booking.date} • ⏰ {String(booking.time || '').slice(0, 5) || booking.time}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {booking.payment_summary?.required ? (
                      <span className="rounded-full bg-purple-100 px-2.5 py-1 text-[11px] font-semibold text-purple-700">
                        Payment: {booking.payment_summary.status}
                      </span>
                    ) : (
                      <span className="rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-semibold text-gray-600">
                        Payment: not required
                      </span>
                    )}
                    {booking.proposal_summary?.status && booking.proposal_summary.status !== 'none' ? (
                      <span className="rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-semibold text-amber-700">
                        Proposal: {booking.proposal_summary.status}
                      </span>
                    ) : null}
                    {booking.tip_summary?.status === 'succeeded' ? (
                      <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
                        Tip: {formatCurrencyFromCents(booking.tip_summary.amount_cents, booking.tip_summary.currency)}
                      </span>
                    ) : null}
                    {booking.balance_summary?.status === 'succeeded' ? (
                      <span className="rounded-full bg-blue-100 px-2.5 py-1 text-[11px] font-semibold text-blue-700">
                        Service paid
                      </span>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>

      {selectedBooking && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-3 sm:px-4">
          <div className="relative max-h-[calc(100dvh-1.5rem)] w-full max-w-2xl overflow-y-auto overscroll-contain rounded-xl bg-white p-4 shadow-2xl sm:p-6">
            <button onClick={handleCloseModal} aria-label="Close appointment details" className="absolute right-2 top-2 inline-flex min-h-11 min-w-11 items-center justify-center rounded-full text-2xl text-gray-500 hover:bg-gray-100 hover:text-red-500 sm:right-3 sm:top-3">✖</button>
            <h2 className="mb-4 pr-10 text-xl font-bold text-[#6D3B7E] sm:text-2xl">📋 Appointment Details</h2>
            {isModalLoading ? <InlineFeedback type="info" message="Loading details..." /> : null}

            <div className="space-y-2 text-sm">
              <p><strong>Client:</strong> {selectedBooking.client?.first_name} {selectedBooking.client?.last_name}</p>
              <p><strong>Date:</strong> {selectedBooking.date}</p>
              <p><strong>Time:</strong> {selectedBooking.time}</p>
              <p><strong>Status:</strong> {selectedBooking.status.current_status}</p>
            </div>

            <div className="mt-4 rounded-lg bg-[#F9F7FC] p-3 text-sm border border-[#EADCF1]">
              <p className="font-semibold text-[#6D3B7E]">Policy</p>
              <p>{selectedBooking.lifecycle_policy?.cancel_message}</p>
              <p>{selectedBooking.lifecycle_policy?.reschedule_message}</p>
            </div>

            <div className="mt-4 rounded-lg bg-[#F4F7FF] p-3 text-sm border border-[#DCE6FF] space-y-1">
              <p className="font-semibold text-[#304A8A]">Payment</p>
              <p>
                Status: <strong>{selectedBooking.payment_summary?.status || 'not_required'}</strong>
              </p>
              {selectedBooking.payment_summary?.required ? (
                <>
                  <p>Total: {formatCurrencyFromCents(
                    selectedBooking.payment_summary?.total_amount_cents,
                    selectedBooking.payment_summary?.currency,
                  )}</p>
                  <p>Deposit: {formatCurrencyFromCents(
                    selectedBooking.payment_summary?.deposit_amount_cents,
                    selectedBooking.payment_summary?.currency,
                  )}</p>
                  {selectedBooking.payment_summary?.payment_due_at
                  && !['succeeded', 'canceled'].includes(selectedBooking.payment_summary?.status) ? (
                    <p>Payment deadline: {new Date(selectedBooking.payment_summary.payment_due_at).toLocaleString()}</p>
                  ) : null}
                  {Number(selectedBooking.payment_summary?.refunded_amount_cents || 0) > 0 ? (
                    <p>Refunded: {formatCurrencyFromCents(
                      selectedBooking.payment_summary.refunded_amount_cents,
                      selectedBooking.payment_summary?.currency,
                    )}</p>
                  ) : null}
                  {selectedBooking.payment_summary?.refund_status !== 'none' ? (
                    <p>Refund: <strong>{selectedBooking.payment_summary.refund_status}</strong></p>
                  ) : null}
                </>
              ) : (
                <p>Deposit not required for this booking.</p>
              )}

              {role === 'client'
              && selectedBooking.payment_summary?.required
              && ['pending', 'failed'].includes(selectedBooking.payment_summary?.status)
              && selectedBooking.status?.current_status !== 'canceled'
              && !paymentCheckout ? (
                <button
                  disabled={isPaymentSubmitting}
                  onClick={handleInitializePayment}
                  className="mt-2 bg-[#304A8A] hover:bg-[#263c70] text-white px-4 py-2 rounded-full"
                >
                  {isPaymentSubmitting ? 'Initializing…' : 'Initialize Deposit Payment'}
                </button>
              ) : null}

              {paymentCheckout?.payment_type === 'deposit' && paymentCheckout?.payment?.client_secret ? (
                <div className="mt-3 rounded-lg border border-[#DCE6FF] bg-white p-3">
                  <StripePaymentForm
                    publishableKey={paymentCheckout.publishable_key}
                    clientSecret={paymentCheckout.payment.client_secret}
                    bookingId={paymentCheckout.booking_id}
                    amountCents={paymentCheckout.payment.amount_cents}
                    currency={paymentCheckout.payment.currency}
                    onPaymentSubmitted={handleStripePaymentSubmitted}
                    paymentType="deposit"
                  />
                </div>
              ) : null}
            </div>

            {selectedBooking.payment_summary?.status === 'succeeded' ? (
              <div className="mt-3 rounded-lg border border-[#E7D9BC] bg-[#FFF9ED] p-3 text-sm text-[#6E5425]">
                <p>{selectedBooking.lifecycle_policy?.deposit_cancel_message}</p>
                <p>{selectedBooking.lifecycle_policy?.deposit_reschedule_message}</p>
              </div>
            ) : null}

            {role === 'client' && selectedBooking.status?.current_status === 'completed' ? (
              <section className="mt-4 rounded-2xl border border-[#DCE6FF] bg-gradient-to-br from-[#F4F7FF] to-white p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-[#526BA3]">Completed service</p>
                    <h3 className="mt-1 text-lg font-semibold text-[#304A8A]">Remaining balance</h3>
                    <p className="text-sm text-gray-600">Pay the service balance securely before or separately from an optional tip.</p>
                  </div>
                  <span aria-hidden="true" className="text-3xl">✓</span>
                </div>

                <dl className="mt-3 grid grid-cols-2 gap-2 text-sm">
                  <div className="rounded-xl bg-white p-3">
                    <dt className="text-gray-600">Service total</dt>
                    <dd className="font-semibold text-[#304A8A]">{formatCurrencyFromCents(
                      selectedBooking.balance_summary?.service_total_cents,
                      selectedBooking.balance_summary?.currency,
                    )}</dd>
                  </div>
                  <div className="rounded-xl bg-white p-3">
                    <dt className="text-gray-600">Paid toward service</dt>
                    <dd className="font-semibold text-[#304A8A]">{formatCurrencyFromCents(
                      selectedBooking.balance_summary?.service_paid_cents,
                      selectedBooking.balance_summary?.currency,
                    )}</dd>
                  </div>
                </dl>

                {selectedBooking.balance_summary?.status === 'succeeded'
                || Number(selectedBooking.balance_summary?.amount_due_cents || 0) === 0 ? (
                  <p className="mt-3 rounded-xl bg-emerald-50 p-3 text-sm font-medium text-emerald-800">
                    Service balance paid in full.
                  </p>
                ) : selectedBooking.balance_summary?.can_pay ? (
                  <div className="mt-3 space-y-3">
                    <div className="flex items-center justify-between rounded-xl bg-white p-3">
                      <span className="text-sm text-gray-600">Amount due</span>
                      <strong className="text-lg text-[#304A8A]">{formatCurrencyFromCents(
                        selectedBooking.balance_summary?.amount_due_cents,
                        selectedBooking.balance_summary?.currency,
                      )}</strong>
                    </div>
                    {!paymentCheckout ? (
                      <button
                        type="button"
                        disabled={isFinalPaymentSubmitting}
                        onClick={handleInitializeFinalPayment}
                        className="w-full rounded-xl bg-[#304A8A] px-4 py-2 font-semibold text-white hover:bg-[#263C70] disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {isFinalPaymentSubmitting ? 'Preparing…' : 'Pay remaining balance'}
                      </button>
                    ) : null}
                    {paymentCheckout?.payment_type === 'final' && paymentCheckout?.payment?.client_secret ? (
                      <div className="rounded-xl border border-[#DCE6FF] bg-white p-3">
                        <StripePaymentForm
                          publishableKey={paymentCheckout.publishable_key}
                          clientSecret={paymentCheckout.payment.client_secret}
                          bookingId={paymentCheckout.booking_id}
                          amountCents={paymentCheckout.payment.amount_cents}
                          currency={paymentCheckout.payment.currency}
                          onPaymentSubmitted={handleStripePaymentSubmitted}
                          paymentType="final"
                        />
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <p className="mt-3 text-sm text-gray-600">Balance status: {selectedBooking.balance_summary?.status || 'not available'}</p>
                )}
              </section>
            ) : null}

            {role === 'client'
            && (selectedBooking.status?.current_status === 'completed' || selectedBooking.tip_summary?.status === 'succeeded') ? (
              <section className="mt-4 rounded-2xl border border-[#E8DDF0] bg-gradient-to-br from-[#FBF7FD] to-white p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-[#8A5A98]">After-service appreciation</p>
                    <h3 className="mt-1 text-lg font-semibold text-[#4B2A56]">Leave a tip</h3>
                    <p className="text-sm text-gray-600">Tips are optional and go through Stripe’s secure checkout.</p>
                  </div>
                  <span aria-hidden="true" className="text-3xl">💜</span>
                </div>

                {selectedBooking.tip_summary?.status === 'succeeded' ? (
                  <div className="mt-3 rounded-xl bg-emerald-50 p-3 text-sm text-emerald-800">
                    Thank you! Your {formatCurrencyFromCents(
                      selectedBooking.tip_summary.amount_cents,
                      selectedBooking.tip_summary.currency,
                    )} tip was received.
                  </div>
                ) : selectedBooking.tip_summary?.can_tip ? (
                  <div className="mt-4 space-y-3">
                    <div className="grid grid-cols-3 gap-2">
                      {[15, 20, 25].map((percent) => {
                        const suggestedCents = Math.max(
                          Math.round(Number(selectedBooking.payment_summary?.total_amount_cents || 0) * percent / 100),
                          100,
                        );
                        const suggestedValue = (suggestedCents / 100).toFixed(2);
                        return (
                          <button
                            key={percent}
                            type="button"
                            onClick={() => setTipAmount(suggestedValue)}
                            className={`rounded-xl border px-2 py-3 text-center transition ${
                              tipAmount === suggestedValue
                                ? 'border-[#6D3B7E] bg-[#6D3B7E] text-white'
                                : 'border-[#DDCDE4] bg-white text-[#5B3965] hover:border-[#9B6BA8]'
                            }`}
                          >
                            <span className="block font-bold">{percent}%</span>
                            <span className="block text-xs">{formatCurrencyFromCents(suggestedCents, selectedBooking.tip_summary.currency)}</span>
                          </button>
                        );
                      })}
                    </div>
                    <label htmlFor={`tip-amount-${selectedBooking.id}`} className="block text-sm font-semibold text-[#4B2A56]">
                      Custom tip amount
                    </label>
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-semibold text-gray-600">$</span>
                      <input
                        id={`tip-amount-${selectedBooking.id}`}
                        type="number"
                        min="1"
                        max="500"
                        step="0.01"
                        inputMode="decimal"
                        value={tipAmount}
                        onChange={(event) => setTipAmount(event.target.value)}
                        placeholder="0.00"
                        className="min-w-0 flex-1 rounded-xl border border-[#DDCDE4] px-3 py-2 text-base"
                      />
                      <button
                        type="button"
                        disabled={isTipSubmitting || !tipAmount || Boolean(paymentCheckout)}
                        onClick={handleInitializeTip}
                        className="rounded-xl bg-[#6D3B7E] px-4 py-2 font-semibold text-white hover:bg-[#593067] disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {isTipSubmitting ? 'Preparing…' : 'Continue'}
                      </button>
                    </div>

                    {paymentCheckout?.payment_type === 'tip' && paymentCheckout?.payment?.client_secret ? (
                      <div className="rounded-xl border border-[#DDCDE4] bg-white p-3">
                        <StripePaymentForm
                          publishableKey={paymentCheckout.publishable_key}
                          clientSecret={paymentCheckout.payment.client_secret}
                          bookingId={paymentCheckout.booking_id}
                          amountCents={paymentCheckout.payment.amount_cents}
                          currency={paymentCheckout.payment.currency}
                          onPaymentSubmitted={handleStripePaymentSubmitted}
                          paymentType="tip"
                        />
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <p className="mt-3 text-sm text-gray-600">Tip status: {selectedBooking.tip_summary?.status || 'not available'}</p>
                )}
              </section>
            ) : null}

            {modalService && (
              <div className="mt-4 border-t pt-4 text-sm space-y-1">
                <h3 className="text-lg font-semibold text-[#6D3B7E]">Service: {modalService.name}</h3>
                <p>{modalService.description}</p>
                <p><strong>Booked total:</strong> {formatCurrencyFromCents(selectedBooking.pricing_summary?.total_amount_cents ?? selectedBooking.total_amount_cents, selectedBooking.currency)}</p>
                <p><strong>Booked duration:</strong> {selectedBooking.duration_summary?.total_minutes || selectedBooking.duration_minutes || modalService.duration} mins</p>
                {Number(selectedBooking.pricing_summary?.attribute_amount_cents || 0) > 0 ? (
                  <p><strong>Selected-option charges:</strong> {formatCurrencyFromCents(selectedBooking.pricing_summary.attribute_amount_cents, selectedBooking.currency)}</p>
                ) : null}
              </div>
            )}

            {(selectedBooking.selection_breakdown || []).length > 0 && (
              <div className="mt-4 rounded-xl border border-[#EADCF1] bg-[#FBF7FD] p-4 text-sm">
                <h4 className="font-semibold text-[#6D3B7E]">Priced selections</h4>
                <ul className="mt-2 space-y-2">
                  {selectedBooking.selection_breakdown.map((line, index) => (
                    <li key={`${line.option_id || index}-${index}`} className="flex items-start justify-between gap-3 rounded-lg bg-white p-2">
                      <span><strong>{line.attribute_name}:</strong> {line.option_label}{line.extra_duration_minutes ? ` (+${line.extra_duration_minutes} min)` : ''}</span>
                      <strong>{line.extra_amount_cents ? `+${formatCurrencyFromCents(line.extra_amount_cents, selectedBooking.currency)}` : 'Included'}</strong>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {(selectedBooking.attribute_responses || []).length > 0 && (
              <div className="mt-4 border-t pt-4 text-sm space-y-1">
                <h4 className="text-lg font-semibold text-[#6D3B7E]">Service details</h4>
                <ul className="list-disc list-inside">
                  {selectedBooking.attribute_responses.map((response) => (
                    <li key={response.id}>
                      <strong>{response.attribute_name}:</strong> {(response.responses || []).join(', ') || 'No'}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {(selectedBooking.allowed_status_transitions || []).length > 0 && (
              <div className="mt-6 border-t pt-4">
                <h4 className="font-semibold text-sm mb-2">🔄 Update Booking Status</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedBooking.allowed_status_transitions
                    .filter((nextStatus) => nextStatus !== 'canceled')
                    .map((nextStatus) => (
                    <button
                      key={nextStatus}
                      disabled={isActionSubmitting}
                      onClick={() => handleUpdateStatus(selectedBooking.id, nextStatus)}
                      className={`text-white px-4 py-2 rounded-full ${
                        'bg-[#6D3B7E] hover:bg-[#5d316d]'
                      }`}
                    >
                      {statusActionLabels[nextStatus] || `Set ${nextStatus}`}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {canShowCancelSection && (
              <div className="mt-6 border-t pt-4 space-y-3">
                <h4 className="font-semibold text-sm">❌ Cancel Booking</h4>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  className="w-full border rounded p-2 text-sm"
                  placeholder="Optional reason for cancellation"
                  rows={2}
                />
                <button
                  disabled={isActionSubmitting || !canSubmitCancel}
                  onClick={handleCancelBooking}
                  className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-full"
                >
                  Cancel this booking
                </button>
                {!canSubmitCancel ? (
                  <p className="text-xs text-gray-500">
                    {cancelBlockedReason}
                  </p>
                ) : null}
              </div>
            )}

            {canRejectPendingBooking ? (
              <div className="mt-6 border-t pt-4 space-y-3">
                <h4 className="font-semibold text-sm">🛑 Reject Pending Booking</h4>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  className="w-full border rounded p-2 text-sm"
                  placeholder="Optional reason for rejection"
                  rows={2}
                />
                <button
                  disabled={isActionSubmitting}
                  onClick={handleRejectBooking}
                  className="bg-[#6D3B7E] hover:bg-[#5d316d] text-white px-4 py-2 rounded-full"
                >
                  Reject this pending booking
                </button>
              </div>
            ) : null}

            {selectedBooking.can_reschedule && (
              <div className="mt-6 border-t pt-4 space-y-3">
                <h4 className="font-semibold text-sm">📅 Reschedule Booking</h4>
                <p className="text-xs text-gray-600">
                  Rescheduling is limited to this worker&apos;s currently available schedule.
                </p>

                {isRescheduleOptionsLoading ? (
                  <p className="text-sm text-gray-500">Loading available worker slots…</p>
                ) : rescheduleAvailableDates.length === 0 ? (
                  <p className="text-sm text-red-600">No available worker slots were found for rescheduling.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <select
                      value={rescheduleDate}
                      onChange={(e) => handleRescheduleDateChange(e.target.value)}
                      className="border rounded p-2 text-sm"
                    >
                      {rescheduleAvailableDates.map((dateOption) => (
                        <option key={dateOption} value={dateOption}>{dateOption}</option>
                      ))}
                    </select>

                    <select
                      value={rescheduleTime}
                      onChange={(e) => setRescheduleTime(e.target.value)}
                      className="border rounded p-2 text-sm"
                    >
                      {rescheduleAvailableTimes.map((timeOption) => (
                        <option key={timeOption} value={timeOption}>{timeOption}</option>
                      ))}
                    </select>
                  </div>
                )}

                {rescheduleOptionsError ? (
                  <p className="text-sm text-red-600">❌ {rescheduleOptionsError}</p>
                ) : null}

                <textarea
                  value={rescheduleReason}
                  onChange={(e) => setRescheduleReason(e.target.value)}
                  className="w-full border rounded p-2 text-sm"
                  placeholder="Optional reason for rescheduling"
                  rows={2}
                />
                <button
                  disabled={
                    isActionSubmitting
                    || isRescheduleOptionsLoading
                    || !rescheduleDate
                    || !rescheduleTime
                    || rescheduleAvailableDates.length === 0
                  }
                  onClick={handleRescheduleBooking}
                  className="bg-[#CDA349] hover:bg-[#b8913e] text-white px-4 py-2 rounded-full"
                >
                  Reschedule booking
                </button>
              </div>
            )}

            {['pending', 'confirmed'].includes(selectedBooking.status.current_status) ? (
              <div className="mt-6 border-t pt-4 space-y-3">
                <h4 className="font-semibold text-sm">🕘 Propose New Date &amp; Time</h4>
                <p className="text-xs text-gray-600">
                  Client and worker can propose a new date/time. The other party can approve or reject the active proposal.
                </p>

                {selectedBooking.proposal_summary?.status && selectedBooking.proposal_summary.status !== 'none' ? (
                  <div className="rounded-lg bg-[#FFF8E7] border border-[#F3DEAA] p-3 text-sm space-y-1">
                    <p><strong>Current Proposal:</strong> {selectedBooking.proposal_summary.status}</p>
                    <p>
                      <strong>Proposed Time:</strong>{' '}
                      {selectedBooking.proposal_summary.proposed_date || 'N/A'}{' '}
                      {String(selectedBooking.proposal_summary.proposed_time || '').slice(0, 5) || 'N/A'}
                    </p>
                    {selectedBooking.proposal_summary?.proposed_by ? (
                      <p>
                        <strong>Proposed By:</strong>{' '}
                        {selectedBooking.proposal_summary.proposed_by.first_name || ''}{' '}
                        {selectedBooking.proposal_summary.proposed_by.last_name || ''}
                        {` (${selectedBooking.proposal_summary.proposed_by.role})`}
                      </p>
                    ) : null}
                    {selectedBooking.proposal_summary?.reason ? (
                      <p><strong>Reason:</strong> {selectedBooking.proposal_summary.reason}</p>
                    ) : null}
                  </div>
                ) : null}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input
                    type="date"
                    value={proposalDate}
                    onChange={(e) => setProposalDate(e.target.value)}
                    className="border rounded p-2 text-sm"
                  />
                  <input
                    type="time"
                    value={proposalTime}
                    onChange={(e) => setProposalTime(e.target.value)}
                    className="border rounded p-2 text-sm"
                  />
                </div>

                <textarea
                  value={proposalReason}
                  onChange={(e) => setProposalReason(e.target.value)}
                  className="w-full border rounded p-2 text-sm"
                  placeholder="Optional reason for proposal"
                  rows={2}
                />

                <button
                  disabled={isProposalSubmitting || !proposalDate || !proposalTime}
                  onClick={handleProposeReschedule}
                  className="bg-[#2F855A] hover:bg-[#276749] text-white px-4 py-2 rounded-full"
                >
                  {isProposalSubmitting ? 'Submitting proposal…' : 'Propose new time'}
                </button>

                {selectedBooking.proposal_summary?.status === 'pending'
                && selectedBooking.proposal_summary?.can_respond ? (
                  <div className="rounded-lg bg-[#F1F5FF] border border-[#DCE6FF] p-3 space-y-2">
                    <p className="text-sm font-semibold text-[#304A8A]">Respond to current proposal</p>
                    <textarea
                      value={proposalResponseReason}
                      onChange={(e) => setProposalResponseReason(e.target.value)}
                      className="w-full border rounded p-2 text-sm"
                      placeholder="Optional response reason"
                      rows={2}
                    />
                    <div className="flex flex-wrap gap-2">
                      <button
                        disabled={isProposalSubmitting}
                        onClick={() => handleRespondToProposal('approve')}
                        className="bg-[#1D4ED8] hover:bg-[#1E40AF] text-white px-4 py-2 rounded-full"
                      >
                        Approve proposal
                      </button>
                      <button
                        disabled={isProposalSubmitting}
                        onClick={() => handleRespondToProposal('reject')}
                        className="bg-[#B45309] hover:bg-[#92400E] text-white px-4 py-2 rounded-full"
                      >
                        Reject proposal
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            {actionError && (
              <p className="mt-4 text-sm text-red-600">❌ {actionError}</p>
            )}

            <div className="mt-6 border-t pt-4">
              <h4 className="font-semibold text-sm mb-2">🕒 Status Timeline</h4>
              {(selectedBooking.status?.status_history || []).length === 0 ? (
                <p className="text-sm text-gray-500">No history yet.</p>
              ) : (
                <ul className="space-y-2 text-sm">
                  {(selectedBooking.status?.status_history || []).map((event, idx) => (
                    <li key={`${event.timestamp}-${idx}`} className="border rounded p-2 bg-gray-50">
                      <p>
                        <strong>{event.event_type || 'status_change'}</strong> •
                        {' '}<span>{event.previous_status || event.old_status} → {event.new_status || event.status}</span>
                      </p>
                      <p>
                        By <strong>{event.actor_username || event.changed_by || 'Unknown'}</strong>
                        {event.actor_role ? ` (${event.actor_role})` : ''}
                        {' '}at {event.timestamp}
                      </p>
                      {event.reason ? <p>Reason: {event.reason}</p> : null}
                      {event.event_type === 'reschedule' ? (
                        <p>
                          {event.previous_date} {event.previous_time} → {event.new_date} {event.new_time}
                        </p>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {selectedBooking.status.current_status === 'completed' && role === 'client' && (
              selectedBooking.review ? (
                <div className="mt-6 border-t pt-4 text-sm">
                  <h4 className="font-semibold text-[#6D3B7E] mb-2">⭐ Your Review</h4>
                  <p><strong>Rating:</strong> {selectedBooking.review.rating} / 5</p>
                  <p><strong>Comment:</strong> {selectedBooking.review.comment}</p>
                </div>
              ) : (
                <form onSubmit={handleReviewSubmit} className="mt-6 space-y-3 border-t pt-4">
                  <h4 className="font-semibold text-sm">✍️ Leave a Review</h4>
                  <div className="flex items-center gap-2">
                    <select value={reviewRating} onChange={e => setReviewRating(Number(e.target.value))} className="border px-2 py-1 rounded">
                      {[1, 2, 3, 4, 5].map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                    <input
                      type="text"
                      value={reviewText}
                      onChange={e => setReviewText(e.target.value)}
                      placeholder="Write a review..."
                      className="flex-1 border px-3 py-1 rounded"
                      required
                    />
                  </div>
                  <button type="submit" className="bg-[#10B981] text-white px-4 py-2 rounded-full hover:bg-[#0e9f6e]">
                    📤 Submit
                  </button>
                </form>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MyBookings;
